import { type ReactNode } from "react";

/** Renderer for the plain-language settings summary.
 *
 *  Deliberately not a Markdown library. The only Markdown this app ever renders is written by one prompt, and that
 *  prompt is told exactly which constructs it may use — headings, paragraphs, flat bullet lists, bold and italic and
 *  inline code. Supporting more would be supporting inputs that do not occur; supporting less would be a summary with
 *  literal asterisks in it. The vocabulary here and the vocabulary in `SettingsAssistantPrompt.summarySystemPrompt`
 *  are one decision and have to move together.
 *
 *  Everything goes through React as text. There is no `dangerouslySetInnerHTML` anywhere in this file, so a summary
 *  that came back with markup in it renders as the markup rather than as behaviour — which matters more than usual
 *  here, because the text is generated from strings a practice can type into its own settings.
 */

type Block =
  | { kind: "heading"; level: 2 | 3; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

/** Group lines into blocks.
 *
 *  A blank line ends whatever is open. A list runs until a line that is not a bullet, so a bullet immediately
 *  following a paragraph starts a list rather than continuing the sentence.
 */
export const parseBlocks = (markdown: string): Block[] => {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length > 0) {
      blocks.push({ kind: "list", items: list });
      list = [];
    }
  };
  const flush = () => {
    flushParagraph();
    flushList();
  };

  for (const raw of markdown.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trimEnd();

    if (line.trim() === "") {
      flush();
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      // Everything above `###` renders as `##`, everything below as `###`. The page owns the `#` level, and a summary
      // that reached for `####` wanted a sub-heading rather than a sixth typographic tier.
      blocks.push({
        kind: "heading",
        level: heading[1].length <= 2 ? 2 : 3,
        text: heading[2].trim(),
      });
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet) {
      flushParagraph();
      list.push(bullet[1].trim());
      continue;
    }

    // An indented line under an open bullet continues it. Without this a wrapped bullet closes the list, becomes a
    // paragraph, and the next bullet opens a second list — one item rendered as body text between two stubs.
    if (list.length > 0 && /^\s+\S/.test(raw)) {
      list[list.length - 1] = `${list[list.length - 1]} ${line.trim()}`;
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }

  flush();
  return blocks;
};

/** Split one line into runs of plain text, bold, italic and code.
 *
 *  One pass, alternation over the four openers, so a `**bold**` is never re-examined for the `*italic*` inside it.
 *  Unmatched markers stay as literal characters: a practice whose FAQ answer contains an asterisk should read as an
 *  asterisk, not swallow the rest of the sentence into an emphasis that never closes.
 */
export const parseInline = (text: string): ReactNode[] => {
  const out: ReactNode[] = [];
  // The `\S` lookahead pins the opening marker to non-space; the closing side uses a lazy run that may be a single
  // character. An earlier version required the run to end in `\S` *in addition* to containing one, which needs two
  // characters — so `**9**` fell through to the italic branch and rendered as `*` + EM("9*"), and two single-character
  // bold runs on one line merged into one that swallowed the text between them. Digits and single letters are exactly
  // what a practice's summary emphasises: keypad numbers, block counts, language codes.
  //
  // A backslash escape is consumed first, so `nothing\_further` reads as written rather than as a stray backslash.
  // The model produces these unasked — escaping a `_` in an identifier is ordinary Markdown politeness — and the
  // renderer refusing to understand its own dialect made an admin summary read like a broken template.
  const pattern = /(\\([\\`*_{}[\]()#+\-.!])|\*\*(?=\S)([^*]+?)\*\*|\*(?=\S)([^*]+?)\*|`([^`]+)`)/g;
  let cursor = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) out.push(text.slice(cursor, match.index));

    if (match[2] !== undefined) {
      // An escaped punctuation mark: emit the character alone, without its backslash.
      out.push(match[2]);
    } else if (match[3] !== undefined) {
      out.push(
        <strong key={key++} className="font-semibold text-gray-900">
          {match[3]}
        </strong>,
      );
    } else if (match[4] !== undefined) {
      out.push(
        <em key={key++} className="italic">
          {match[4]}
        </em>,
      );
    } else {
      out.push(
        <code key={key++} className="rounded bg-gray-100 px-1 py-0.5 text-[12px] text-gray-700">
          {match[5]}
        </code>,
      );
    }
    cursor = pattern.lastIndex;
  }

  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
};

export const Markdown = ({ source }: { source: string }) => {
  const blocks = parseBlocks(source);

  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        if (block.kind === "heading") {
          return block.level === 2 ? (
            <h3
              key={i}
              className="border-b border-gray-100 pb-1 text-sm font-semibold text-[#1a2b6b] first:mt-0"
            >
              {parseInline(block.text)}
            </h3>
          ) : (
            <h4 key={i} className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {parseInline(block.text)}
            </h4>
          );
        }
        if (block.kind === "list") {
          return (
            <ul key={i} className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-gray-700">
              {block.items.map((item, j) => (
                <li key={j}>{parseInline(item)}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="text-sm leading-relaxed text-gray-700">
            {parseInline(block.text)}
          </p>
        );
      })}
    </div>
  );
};
